import React from "react";
import { useNavigate } from "react-router-dom";
import {
  LIBELLES_STATUT, LIBELLES_ROLE_PROJET, classeStatutProjet, formatDate, formatHeures, largeurJauge,
} from "../services/projet";

// ===================================================================
// The Crowd v2 — Pilotage › Projets : blocs d'affichage
//
// - VueCollaborateurs : une carte par acteur principal (nombre de
//   projets, avancement global, jalons, retards, temps), puis une
//   sous-carte par projet ;
// - VueListe : lecture par collaborateur, puis tableau des projets ;
// - JalonsAConfirmer : propositions en attente du chef ou d'un co-chef.
// Donnees : GET /api/pilotage/projets (pilotage_projets.py).
// ===================================================================

export function Jauge({ valeur }) {
  return (
    <div className="v2-projet-jauge">
      <span className="v2-barres-piste">
        <span className="v2-barres-valeur v2-ton-fort" style={{ width: largeurJauge(valeur) }} />
      </span>
      <span className="v2-projet-jauge-valeur">{valeur || 0} %</span>
    </div>
  );
}

function Badges({ p }) {
  return (
    <>
      <span className={classeStatutProjet(p.statut)}>{LIBELLES_STATUT[p.statut] || p.statut}</span>
      {p.en_retard && <span className="v2-statut v2-statut-danger">En retard</span>}
      {p.jalons_proposes > 0 && <span className="v2-statut v2-statut-warning">Jalon à confirmer</span>}
      {p.mon_role && <span className="v2-statut">{LIBELLES_ROLE_PROJET[p.mon_role]}</span>}
    </>
  );
}

function ResumeEtapes({ p }) {
  if (!p.etapes_total) return <span className="v2-cellule-discrete">Aucune étape</span>;
  return (
    <span className="v2-cellule-discrete">
      {p.etapes_terminees} / {p.etapes_total} étapes
      {p.etapes_en_retard > 0 && <span className="v2-texte-danger"> · {p.etapes_en_retard} en retard</span>}
    </span>
  );
}

function initiales(nom) {
  return (nom || "?").split(" ").filter(Boolean).slice(0, 2).map((m) => m[0].toUpperCase()).join("");
}

function libelleRetards(c) {
  const morceaux = [];
  if (c.projets_en_retard > 0) morceaux.push(`${c.projets_en_retard} projet${c.projets_en_retard > 1 ? "s" : ""}`);
  if (c.etapes_en_retard > 0) morceaux.push(`${c.etapes_en_retard} étape${c.etapes_en_retard > 1 ? "s" : ""}`);
  return morceaux.join(", ");
}

// --- Vue par collaborateur ---

function SousCarteProjet({ p, onOuvrir }) {
  return (
    <article className="v2-pilotage-sous-carte">
      <button type="button" className="v2-pilotage-sous-carte-ouvrir" onClick={onOuvrir}>
        <span className="v2-pilotage-sous-carte-nom">{p.nom}</span>
        <span className="v2-mes-badges"><Badges p={p} /></span>
        <Jauge valeur={p.avancement} />
        <ResumeEtapes p={p} />
        <span className="v2-cellule-discrete">
          Jalons : {p.jalons.length ? `${p.jalons_atteints} / ${p.jalons.length}` : "aucun"}
          {" · "}Temps : {formatHeures(p.heures_etapes)}
          {" · "}Fin : {formatDate(p.date_fin_prevue)}
        </span>
      </button>
    </article>
  );
}

function CarteCollaborateur({ groupe, onOuvrirProjet, onOuvrirCollaborateur }) {
  const { stats, projets } = groupe;
  const retards = libelleRetards(stats);

  return (
    <section className="v2-pilotage-collab" aria-label={stats.nom}>
      <header className="v2-pilotage-collab-entete">
        <div className="v2-collab-entete">
          <span className="v2-collab-avatar" aria-hidden="true">{stats.collaborateur_id ? initiales(stats.nom) : "—"}</span>
          <div className="v2-collab-identite">
            {stats.collaborateur_id ? (
              <button type="button" className="v2-lien-ligne v2-collab-nom" onClick={() => onOuvrirCollaborateur(stats.collaborateur_id)}>
                {stats.nom}
              </button>
            ) : (
              <span className="v2-collab-nom">{stats.nom}</span>
            )}
            <span className="v2-collab-profil">
              {projets.length} projet{projets.length > 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <div className="v2-pilotage-collab-synthese">
          <span className="v2-label">Avancement global</span>
          <Jauge valeur={stats.avancement_moyen} />
        </div>
        <div className="v2-pilotage-collab-chiffres">
          <span>Jalons atteints : <strong>{stats.jalons_total ? `${stats.jalons_atteints} / ${stats.jalons_total}` : "—"}</strong></span>
          <span>Temps : <strong>{formatHeures(stats.heures)}</strong></span>
          {retards && <span className="v2-texte-danger">En retard : {retards}</span>}
        </div>
      </header>
      <div className="v2-pilotage-sous-grille">
        {projets.map((p) => <SousCarteProjet key={p.id} p={p} onOuvrir={() => onOuvrirProjet(p.id)} />)}
      </div>
    </section>
  );
}

// Regroupe les projets par acteur principal, dans l'ordre de par_collaborateur ;
// les projets sans acteur principal forment un dernier groupe.
function regrouper(projets, parCollaborateur) {
  const groupes = parCollaborateur.map((c) => ({
    stats: c,
    projets: projets.filter((p) => p.acteur_principal_id === c.collaborateur_id),
  }));
  const orphelins = projets.filter((p) => !p.acteur_principal_id);
  if (orphelins.length > 0) {
    const somme = orphelins.reduce((s, p) => s + (p.avancement || 0), 0);
    groupes.push({
      stats: {
        collaborateur_id: null,
        nom: "Sans acteur principal",
        avancement_moyen: Math.round(somme / orphelins.length),
        jalons_atteints: orphelins.reduce((s, p) => s + p.jalons_atteints, 0),
        jalons_total: orphelins.reduce((s, p) => s + p.jalons.length, 0),
        projets_en_retard: orphelins.filter((p) => p.en_retard).length,
        etapes_en_retard: orphelins.reduce((s, p) => s + p.etapes_en_retard, 0),
        heures: orphelins.reduce((s, p) => s + p.heures_etapes, 0),
      },
      projets: orphelins,
    });
  }
  return groupes.filter((g) => g.projets.length > 0);
}

export function VueCollaborateurs({ projets, parCollaborateur, onOuvrirProjet }) {
  const navigate = useNavigate();
  const groupes = regrouper(projets, parCollaborateur);
  return (
    <div className="v2-pilotage-collabs">
      {groupes.map((g) => (
        <CarteCollaborateur
          key={g.stats.collaborateur_id || "sans"}
          groupe={g}
          onOuvrirProjet={onOuvrirProjet}
          onOuvrirCollaborateur={(id) => navigate(`/v2/pilotage/equipe/${id}`)}
        />
      ))}
    </div>
  );
}

// --- Vue liste ---

function ParCollaborateur({ lignes }) {
  return (
    <section className="v2-panneau v2-projet-bloc v2-pilotage-bloc">
      <div className="v2-bloc-entete">
        <h2 className="v2-bloc-titre">Par collaborateur</h2>
      </div>
      <div className="v2-tableau-conteneur">
        <table className="v2-tableau">
          <thead>
            <tr>
              <th scope="col">Acteur principal</th>
              <th scope="col" className="v2-num">Projets</th>
              <th scope="col" className="v2-projet-col-avancement">Avancement global</th>
              <th scope="col" className="v2-num">Jalons atteints</th>
              <th scope="col" className="v2-num">Retards</th>
              <th scope="col" className="v2-num">Temps passé</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((c) => (
              <tr key={c.collaborateur_id}>
                <td className="v2-gras">{c.nom}</td>
                <td className="v2-num">{c.objectifs}</td>
                <td><Jauge valeur={c.avancement_moyen} /></td>
                <td className="v2-num">{c.jalons_total ? `${c.jalons_atteints} / ${c.jalons_total}` : "—"}</td>
                <td className="v2-num">{libelleRetards(c) ? <span className="v2-texte-danger">{libelleRetards(c)}</span> : "—"}</td>
                <td className="v2-num">{formatHeures(c.heures)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function VueListe({ projets, parCollaborateur, onOuvrirProjet }) {
  return (
    <>
      {parCollaborateur.length > 0 && <ParCollaborateur lignes={parCollaborateur} />}
      <div className="v2-panneau v2-tableau-conteneur">
        <table className="v2-tableau v2-tableau-cliquable">
          <thead>
            <tr>
              <th scope="col">Projet</th>
              <th scope="col">Acteur principal</th>
              <th scope="col">Statut</th>
              <th scope="col" className="v2-projet-col-avancement">Avancement</th>
              <th scope="col">Étapes</th>
              <th scope="col" className="v2-num">Jalons</th>
              <th scope="col" className="v2-num">Temps passé</th>
              <th scope="col">Fin prévue</th>
            </tr>
          </thead>
          <tbody>
            {projets.map((p) => (
              <tr key={p.id} onClick={() => onOuvrirProjet(p.id)}>
                <td>
                  <button type="button" className="v2-lien-ligne" onClick={(e) => { e.stopPropagation(); onOuvrirProjet(p.id); }}>{p.nom}</button>
                </td>
                <td className="v2-cellule-discrete">{p.acteur_principal_nom || "—"}</td>
                <td><span className="v2-mes-badges"><Badges p={p} /></span></td>
                <td><Jauge valeur={p.avancement} /></td>
                <td><ResumeEtapes p={p} /></td>
                <td className="v2-num">{p.jalons.length ? `${p.jalons_atteints} / ${p.jalons.length}` : "—"}</td>
                <td className="v2-num">{formatHeures(p.heures_etapes)}</td>
                <td className="v2-cellule-discrete">{formatDate(p.date_fin_prevue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// --- Jalons a confirmer ---

export function JalonsAConfirmer({ jalons, onOuvrirProjet, onConfirmer, onRefuser, enCours }) {
  return (
    <section className="v2-panneau v2-projet-bloc v2-pilotage-bloc">
      <div className="v2-bloc-entete">
        <h2 className="v2-bloc-titre">Jalons à confirmer<span className="v2-projet-compte">{jalons.length}</span></h2>
      </div>
      <ul className="v2-pilotage-jalons">
        {jalons.map((j) => (
          <li key={j.id} className="v2-pilotage-jalon">
            <div className="v2-pilotage-jalon-texte">
              <span className="v2-gras">{j.intitule}</span>
              <span className="v2-cellule-discrete">{j.critere}</span>
              <span className="v2-cellule-discrete">
                <button type="button" className="v2-lien-ligne v2-tache-projet" onClick={() => onOuvrirProjet(j.projet_id)}>{j.projet_nom}</button>
                {" · "}proposé le {formatDate(j.propose_le)}{j.propose_par_nom ? ` par ${j.propose_par_nom}` : ""}
                {j.cible_visee !== null && j.cible_visee !== undefined &&
                  ` · ${j.cible_actuelle ?? "—"} / ${j.cible_visee}${j.cible_unite ? ` ${j.cible_unite}` : ""}`}
              </span>
            </div>
            <div className="v2-jalon-actions">
              {j.peut_confirmer ? (
                <>
                  <button type="button" className="v2-btn v2-btn-principal v2-btn-compact" disabled={enCours === j.id}
                    onClick={() => onConfirmer(j)}>
                    {enCours === j.id ? "…" : "Confirmer"}
                  </button>
                  <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" onClick={() => onRefuser(j)}>
                    Ne pas confirmer
                  </button>
                </>
              ) : (
                <span className="v2-cellule-discrete">Confirmation par le chef du projet</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
